<template>
  <master-layout>
    <div class="flex min-h-full flex-col bg-brown-50">
      <BackButton class="w-full px-[20px] mb-[16px]" :customBack="goBack" />

      <div v-if="isLoading" class="flex flex-1 items-center justify-center pt-20">
        <Spinner size="lg" label="Loading voucher" class="text-grey-60" />
      </div>

      <div v-else-if="voucher" class="flex flex-col px-5 pb-8">
        <!-- Status only. The category tag that sat here read as an instruction
             next to it — "Collection" beside "Available", on a screen that also
             says "to collect" and "Collected" — and grouping is the list's job,
             where it is already the section heading. -->
        <div class="mb-2 flex flex-row items-center gap-2">
          <DsTag size="sm" :variant="statusTagVariant">{{ statusLabel }}</DsTag>
        </div>

        <div class="text-text-heading-md font-black text-grey-100">{{ voucher.name }}</div>

        <div class="text-subheader-1 font-bold text-grey-100 mt-2">
          {{ voucher.value }}
        </div>

        <!-- No placeholder when there is no image: an empty coloured block says
             nothing the heading above it has not already said. -->
        <DsCard v-if="voucher.imageUrl" padding="none" class="mt-4 overflow-hidden">
          <img :src="voucher.imageUrl" :alt="voucher.name" class="h-auto w-full" />
        </DsCard>

        <!-- Cancelled partner pass: kept visible on purpose. The partner treats
             a cancelled order as recoverable, so silently dropping it from the
             wallet would make it vanish and later reappear unexplained. Shown as
             "unavailable" — the member cancelled nothing, and it may come back. -->
        <div v-if="isCancelled" class="mt-6">
          <DsCard padding="md" class="text-center text-text-body-md text-grey-100">
            <div class="font-bold">Currently unavailable</div>
            <div class="text-text-body-small text-grey-60 mt-1">
              This one can't be collected at the moment. Please check with us if you were expecting
              it.
            </div>
          </DsCard>
        </div>

        <!-- Partner collection pass (collected). A book that also unlocks a
             course stays Collected — IDR burned the pass; the member did not
             tap Redeem — and still offers a way into the course. -->
        <div v-else-if="isPartnerPass && isRedeemed" class="mt-6">
          <DsCard padding="md" class="text-center text-text-body-md text-grey-100">
            <DsIcon name="check-circle" size="lg" class="text-status-success mb-2" />
            <div class="font-bold">Collected</div>
            <div v-if="voucher.redeemedAt" class="text-text-body-small text-grey-60 mt-1">
              {{ collectedOnText }}
            </div>
            <div v-if="isCourseUnlock" class="text-text-body-small text-grey-60 mt-2">
              You have full access to this course.
            </div>
          </DsCard>
          <DsButton
            v-if="isCourseUnlock"
            class="w-full mt-4"
            variant="primary"
            size="56"
            shape="pill"
            @click="goToCourse"
          >
            Go to course
          </DsButton>
        </div>

        <!-- Partner collection pass (expired). Ordered AFTER the collected
             branch on purpose: a pass collected before it lapsed was collected,
             and IDR's own status ladder treats redemption as outranking expiry.
             Inert by design — showing the QR here would put a scannable code and
             "Show this to collect" in front of a member for something the
             counter will refuse, which is a wasted trip rather than a wrong
             pixel. -->
        <div v-else-if="isPartnerPass && isVoucherExpired" class="mt-6">
          <DsCard padding="md" class="text-center text-text-body-md text-grey-100">
            <div class="font-bold">Expired</div>
            <div class="text-text-body-small text-grey-60 mt-1">
              This pass can no longer be collected. Please check with us if you were expecting it.
            </div>
          </DsCard>
        </div>

        <!-- Partner collection pass (still to collect). The partner mints and
             burns the code, so there is nothing for us to generate here. -->
        <div v-else-if="isPartnerPass" class="mt-6">
          <PartnerCollectionCode :code="voucher.collectionCode" />

          <!-- The only thing in the wallet that asks the partner. Everything
               else reads our mirror, which the daily sweep updates — so without
               this the card would still say "ready to collect" for hours after
               the counter had burned the code. A button rather than a timer:
               IDR sends no webhook to subscribe to, and polling on the member's
               behalf spends their battery guessing when they are at a counter. -->
          <button
            type="button"
            class="mt-3 flex min-h-[44px] w-full flex-row items-center justify-center gap-2 rounded-full border border-grey-20 text-text-body-small-md font-semibold text-grey-100 transition-transform duration-100 ease-out active:scale-[0.98] disabled:opacity-50 motion-reduce:transition-none"
            :disabled="refreshing || refreshCooldown"
            @click="checkCollected"
          >
            <DsIcon v-if="!refreshing" name="refresh" size="sm" />
            <Spinner v-else size="sm" />
            {{ refreshLabel }}
          </button>
          <div class="sr-only" role="status" aria-live="polite">{{ refreshAnnouncement }}</div>
        </div>

        <!-- Course-unlock voucher (redeemed): access already granted -->
        <div v-else-if="isRedeemed && isCourseUnlock" class="mt-6">
          <DsCard padding="md" class="text-center text-text-body-md text-grey-100">
            <DsIcon name="check-circle" size="lg" class="text-status-success mb-2" />
            <div class="font-bold">Course unlocked</div>
            <div class="text-text-body-small text-grey-60 mt-1">
              You have full access to this course.
            </div>
          </DsCard>
          <DsButton
            class="w-full mt-4"
            variant="primary"
            size="56"
            shape="pill"
            @click="goToCourse"
          >
            Go to course
          </DsButton>
        </div>

        <!-- Course-unlock voucher (available): redeem in-app to grant access -->
        <div v-else-if="isCourseUnlock" class="mt-6">
          <DsButton
            class="w-full"
            variant="primary"
            size="56"
            shape="pill"
            :loading="isRedeeming"
            :disabled="isVoucherExpired"
            @click="handleRedeemCourse"
          >
            Redeem &amp; unlock course
          </DsButton>
          <div
            v-if="isVoucherExpired"
            class="text-text-body-small text-status-error text-center mt-2"
          >
            This voucher has expired.
          </div>
        </div>

        <div v-if="voucher.description" class="mt-6">
          <div class="text-text-caption-md font-semibold uppercase tracking-wide text-grey-60 mb-2">
            Details
          </div>
          <DsCard padding="md" class="text-text-body-md text-grey-100">
            {{ voucher.description }}
          </DsCard>
        </div>

        <!-- A plain line, and nothing at all when there is no expiry: an absent
             deadline is not news, and a card around it competed with the code. -->
        <div
          v-if="expiryText"
          class="mt-4 text-center text-text-body-small"
          :class="isVoucherExpired ? 'text-status-error' : 'text-grey-60'"
        >
          {{ expiryText }}
        </div>
      </div>

      <!-- A load failure (offline / server error) is distinct from a genuinely
           missing voucher — otherwise a network blip reads as "removed". -->
      <div
        v-else-if="loadError"
        class="flex flex-1 flex-col items-center justify-center px-8 pt-20 text-center"
      >
        <DsCard padding="lg" class="w-full">
          <div class="text-text-body-md font-bold text-grey-100">Couldn't load this voucher</div>
          <div class="mt-2 text-text-body-small text-grey-60">{{ loadError }}</div>
          <DsButton class="mt-4 w-full" variant="secondary" size="48" shape="pill" @click="reload">
            Try again
          </DsButton>
        </DsCard>
      </div>

      <div v-else class="flex flex-1 flex-col items-center justify-center px-8 pt-20">
        <DsCard padding="lg" class="w-full text-center">
          <div class="text-text-body-md font-bold text-grey-100">Voucher not found</div>
          <div class="mt-2 text-text-body-small text-grey-60">
            This voucher may have been removed or is no longer available.
          </div>
          <DsButton class="mt-4" variant="secondary" size="48" shape="pill" @click="goBack">
            Back to my vouchers
          </DsButton>
        </DsCard>
      </div>

      <Toast safe-area />
    </div>
  </master-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import dayjs from 'dayjs';
import {
  Button as DsButton,
  Card as DsCard,
  Icon as DsIcon,
  Spinner,
  Tag as DsTag,
} from '@team-hogc/hogc-one-design-system';
import BackButton from '@/components/common/BackButton.vue';
import Toast from '@/components/common/Toast.vue';
import PartnerCollectionCode from '@/components/Vouchers/PartnerCollectionCode.vue';
import {
  expiringSoonText,
  isCancelled,
  isExpired,
  isExpiringSoon,
  isPartnerPass,
  isRedeemed,
  statusLabel,
  statusTagVariant,
} from '@/utils/voucher-expiry';

// Mirrors the server's per-order throttle. Asking faster cannot learn anything
// new — the API answers from its mirror inside that window — so the button
// disables itself rather than making a call that is guaranteed to be stale.
const REFRESH_COOLDOWN_MS = 3000;

export default {
  components: {
    BackButton,
    Toast,
    PartnerCollectionCode,
    DsButton,
    DsCard,
    DsIcon,
    DsTag,
    Spinner,
  },
  data() {
    return {
      // Load failure for THIS voucher, so a network error shows a retry rather
      // than the "Voucher not found" (removed) state.
      loadError: null,
      isRedeeming: false,
      refreshing: false,
      refreshCooldown: false,
      cooldownTimerId: null,
      // What the last press learned, so the member gets an answer either way —
      // "still to collect" is information, and a button that visibly does
      // nothing reads as broken.
      refreshAnnouncement: '',
    };
  },
  computed: {
    ...mapState('vouchers', ['selectedVoucher']),
    ...mapState('profile', { memberId: state => state.personalId }),
    ...mapGetters('vouchers', ['isLoading']),
    voucher() {
      return this.selectedVoucher;
    },
    // Pass-throughs to @/utils/voucher-expiry. The ladder is shared with the card
    // deliberately: this page and the card it was opened from had disagreed about
    // the same voucher twice, and each keeping its own copy is what allowed it.
    isRedeemed() {
      return isRedeemed(this.voucher);
    },
    isCancelled() {
      return isCancelled(this.voucher);
    },
    // Issued by the external collection partner rather than by HOGC: they mint
    // the code and burn it at their own counter, so none of the in-app
    // redemption paths apply.
    isPartnerPass() {
      return isPartnerPass(this.voucher);
    },
    /**
     * Wording for the check button. Names what it does rather than the mechanism
     * — "Refresh" invites the member to wonder what is stale, where "Check if
     * collected" is the question they actually have while standing at a counter.
     */
    refreshLabel() {
      if (this.refreshing) return 'Checking…';
      if (this.refreshCooldown) return 'Just checked';
      return 'Check if collected';
    },
    collectedOnText() {
      return `Collected on ${dayjs(this.voucher.redeemedAt).format('D MMM YYYY')}`;
    },
    // A course-unlock voucher redeems in-app (grants course access) rather than at
    // a physical booth — see the `unlocksProgramSlug` set by the API.
    isCourseUnlock() {
      return Boolean(this.voucher?.unlocksProgramSlug);
    },
    statusLabel() {
      return statusLabel(this.voucher);
    },
    statusTagVariant() {
      return statusTagVariant(this.voucher);
    },
    isVoucherExpired() {
      return isExpired(this.voucher);
    },
    isExpiringSoon() {
      return isExpiringSoon(this.voucher);
    },
    expiryText() {
      // A pass the issuer expired has no date to print, so say the fact instead
      // of nothing — the tag alone leaves the member wondering when.
      if (!this.voucher?.expiresAt) {
        return this.isVoucherExpired ? 'This pass has expired' : '';
      }
      const expiry = dayjs(this.voucher.expiresAt);
      if (expiry.isBefore(dayjs())) {
        return `Expired on ${expiry.format('D MMM YYYY')}`;
      }
      if (isExpiringSoon(this.voucher)) return expiringSoonText(this.voucher);
      return `Expires on ${expiry.format('D MMM YYYY')}`;
    },
  },

  async ionViewWillEnter() {
    await this.reload();
  },
  ionViewWillLeave() {
    this.clearCooldown();
    this.$store.dispatch('vouchers/clearSelectedVoucher');
  },
  beforeUnmount() {
    this.clearCooldown();
  },
  methods: {
    async reload() {
      const voucherId = this.$route.params.id;
      if (!voucherId) return;
      this.loadError = null;
      try {
        await this.$store.dispatch('vouchers/fetchVoucherById', voucherId);
      } catch (error) {
        // A genuine 404 means the voucher was removed — fall through to the
        // "not found" state (leave loadError null). Any other failure (offline /
        // 5xx) shows a retry. Either way the rejection is swallowed here.
        if (error?.response?.status !== 404) {
          this.loadError = error?.response?.data?.message || 'Check your connection and try again.';
        }
      }
    },
    goBack() {
      this.$router.push('/vouchers');
    },
    async handleRedeemCourse() {
      if (!this.voucher?.id || this.isRedeeming) return;
      this.isRedeeming = true;
      try {
        const result = await this.$store.dispatch('vouchers/redeemVoucher', this.voucher.id);
        this.$store.dispatch('toast/success', 'Course unlocked!');
        const slug = result?.unlockedProgramSlug || this.voucher.unlocksProgramSlug;
        if (slug) this.$router.push(`/courses/${slug}`);
      } catch {
        // Error already handled by store action (toast)
      } finally {
        this.isRedeeming = false;
      }
    },
    goToCourse() {
      const slug = this.voucher?.unlocksProgramSlug;
      if (slug) this.$router.push(`/courses/${slug}`);
    },
    /**
     * Asks whether the counter has burned this code yet.
     *
     * IDR's inbound webhook is the primary writer. This button is the fallback
     * while standing at the counter, before the daily sweep would catch up.
     */
    async checkCollected() {
      if (this.refreshing || this.refreshCooldown) return;
      this.refreshing = true;
      this.refreshAnnouncement = '';
      try {
        await this.$store.dispatch('vouchers/refreshClaimStatus', this.voucher.id);
        if (this.isRedeemed) {
          this.refreshAnnouncement = 'Collected';
          this.$store.dispatch(
            'toast/success',
            this.isCourseUnlock ? 'Collected — your course is unlocked' : 'Collected — enjoy it!',
          );
          return;
        }
        // Say so out loud. A button that changes nothing visible reads as
        // broken, and "not yet" is the answer most presses will get.
        this.refreshAnnouncement = 'Not collected yet';
        this.$store.dispatch('toast/info', 'Not collected yet.');
      } catch {
        // The code on screen is still valid and readable without us, so a
        // failed check is not worth an error state — only an honest message.
        this.refreshAnnouncement = 'Could not check just now';
        this.$store.dispatch('toast/error', 'Could not check just now.');
      } finally {
        this.refreshing = false;
        this.startCooldown();
      }
    },
    startCooldown() {
      this.clearCooldown();
      this.refreshCooldown = true;
      this.cooldownTimerId = setTimeout(() => {
        this.refreshCooldown = false;
        this.cooldownTimerId = null;
      }, REFRESH_COOLDOWN_MS);
    },
    clearCooldown() {
      if (this.cooldownTimerId) {
        clearTimeout(this.cooldownTimerId);
        this.cooldownTimerId = null;
      }
      this.refreshCooldown = false;
    },
  },
};
</script>
